import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { ReactComponent as IconHB } from '@svg/common/header-hb.svg';
import { ReactComponent as IconArrowLeft } from '@svg/icon-arrow-left.svg';
import { ReactComponent as IconEditorEdit } from '@svg/icon-editor-edit.svg';
import { ReactComponent as IconEditorChapter } from '@svg/icon-editor-chapter.svg';
import { ReactComponent as IconEditorPublishing } from '@svg/icon-editor-publishing.svg';
import { ReactComponent as IconEditorCoord } from '@svg/icon-editor-coord.svg';

import { useUIStore } from '@stores/ui';
import { scVarTransitionDuration } from '@utils/emotion/variables';
import { scMixinAbsoluteFull, scMixinEllipsis } from '@utils/emotion/mixins';
import { IVideoData } from '@interfaces/video.interface';
import { formatDigitToHHmmss, getSelectedThumbnailItemUrl, getViewerUrlByStage } from '~/utils';

const { Sider: AntdSider } = Layout;

interface IVideoSiderProps {
  data?: IVideoData;
}

const VideoSider: React.FC<IVideoSiderProps> = ({ data }) => {
  const { isSiderCollapsed } = useUIStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  if (!data) {
    return (
      <VideoSiderContainer>
        <div className="sider-header is-skeleton">
          <div className="thumbnail-area">
            <div
              className="image"
              style={{ backgroundImage: `url(/assets/player/default-video.png)` }}
            />
          </div>
          <div className="info-area">
            <strong className="title">&nbsp;</strong>
            <p className="desc">&nbsp;</p>
          </div>
        </div>
        <div className="ant-menu"></div>
      </VideoSiderContainer>
    );
  }

  const { videoId, title, description, type, categories, thumbnails, duration, jobs } = data;
  const { transcoding, poseEstimation } = jobs;
  const hasCategory = categories[0] !== undefined;

  return (
    <VideoSiderContainer>
      <div className="sider-header">
        {data.published ? (
          <Tooltip
            placement={isSiderCollapsed ? 'right' : 'top'}
            title={t('sider.thumbnail.watch')}
          >
            <a
              href={`${getViewerUrlByStage()}/watch?v=${videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="thumbnail-area"
            >
              <div className="overlay" aria-hidden="true">
                <i className="icon">
                  <img
                    src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTEiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAxMSAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTUuMDQ4NDUgOC4yNTY5N0w2Ljc1MTA2IDUuMjE4NzhDNi43NzE0NSA1LjE3OCA2Ljc5MTg0IDUuMTI3MDMgNi43OTE4NCA1LjA3NjA1TDYuODEyMjMgMC4zMDQ2NzVDNi44MTIyMyAtMC4wMTEzNzc5IDYuNDA0NDIgLTAuMTEzMzMgNi4yNTE0OSAwLjE1MTc0NkwwLjAwMTgwMjM1IDExLjMyNTdDLTAuMTEwMzQ1IDExLjUxOTQgNS4wNDg0NSA4LjI1Njk3IDUuMDQ4NDUgOC4yNTY5N1oiIGZpbGw9IiNGNUY2RjgiLz4KPHBhdGggZD0iTTUuMDY4NDYgMTEuNzMzM0wzLjM2NTg2IDE0Ljc4MTdDMy4zNDU0NyAxNC44MjI1IDMuMzI1MDggMTQuODczNSAzLjMyNTA4IDE0LjkyNDRMMy4zMDQ2OSAxOS42OTU4QzMuMzA0NjkgMjAuMDExOSAzLjcxMjUgMjAuMTEzOCAzLjg2NTQzIDE5Ljg0ODdMMTAuMTE1MSA4LjY2NDU1QzEwLjIyNzMgOC40NzA4NCA1LjA2ODQ2IDExLjczMzMgNS4wNjg0NiAxMS43MzMzWiIgZmlsbD0iI0Y1RjZGOCIvPgo8cGF0aCBkPSJNOS44NzQ5OSA4LjI0NjA5SDkuMTUxMTNINS4wODMyMkgxLjc1OTU3TDAuMDQ2NzcyNiAxMS4zMTQ5Qy0wLjA2NTM3NTEgMTEuNTE4OCAwLjA3NzM1ODQgMTEuNzYzNSAwLjMxMTg0OSAxMS43NjM1SDUuMDkzNDJIOC40MjcyN0wxMC4xNDAxIDguNjk0NjhDMTAuMjQyIDguNDkwNzggMTAuMDk5MyA4LjI0NjA5IDkuODc0OTkgOC4yNDYwOVoiIGZpbGw9IiNGNUY2RjgiLz4KPC9zdmc+Cg=="
                    alt="Sparky"
                  />
                </i>
              </div>
              <div
                className="image"
                aria-label={title}
                style={{ backgroundImage: `url(${getSelectedThumbnailItemUrl(thumbnails)})` }}
              />
              {duration > 0 && (
                <div className="duration-box">
                  <span className="duration-value">
                    {formatDigitToHHmmss(duration, true, duration >= 3600, true)}
                  </span>
                </div>
              )}
            </a>
          </Tooltip>
        ) : (
          <div className="thumbnail-area">
            <div
              className="image"
              aria-label={title}
              style={{
                backgroundImage: `url(${
                  getSelectedThumbnailItemUrl(thumbnails) ?? '/assets/player/default-video.png'
                })`,
              }}
            />
            {duration > 0 && (
              <div className="duration-box">
                <span className="duration-value">
                  {formatDigitToHHmmss(duration, true, duration >= 3600, true)}
                </span>
              </div>
            )}
          </div>
        )}
        <div className="info-area">
          <strong className="title">{title}</strong>
          <p className="desc">{description}</p>
        </div>
      </div>
      <Menu
        mode="inline"
        defaultSelectedKeys={['/']}
        selectedKeys={[location.pathname]}
        items={[
          {
            key: `/video/${videoId}/edit`,
            icon: (
              <span className="icon" aria-hidden="true">
                <IconEditorEdit />
              </span>
            ),
            label: t('nav.video.details'),
            onClick: () => {
              navigate(`/video/${videoId}/edit`);
            },
          },
          {
            key: `/video/${videoId}/editor/chapter`,
            icon: (
              <span className="icon" aria-hidden="true">
                <IconEditorChapter />
              </span>
            ),
            label: t('nav.video.chapter'),
            disabled: type === 'explanation' || !hasCategory || transcoding === 'pending',
            onClick: () => {
              navigate(`/video/${videoId}/editor/chapter`);
            },
          },
          {
            key: `/video/${videoId}/visibility`,
            icon: (
              <span className="icon" aria-hidden="true">
                <IconEditorPublishing />
              </span>
            ),
            label: t('nav.video.publishing'),
            disabled: !hasCategory || transcoding === 'pending',
            onClick: () => {
              navigate(`/video/${videoId}/visibility`);
            },
          },
          {
            key: `/video/${videoId}/editor/coords`,
            icon: (
              <span className="icon" aria-hidden="true">
                <IconEditorCoord />
              </span>
            ),
            label: t('nav.video.coords'),
            disabled:
              type === 'explanation' ||
              poseEstimation === 'pending' ||
              poseEstimation === 'processing' ||
              !hasCategory ||
              transcoding === 'pending',
            onClick: () => {
              navigate(`/video/${videoId}/editor/coords`);
            },
          },
        ]}
      />
    </VideoSiderContainer>
  );
};

const VideoSiderContainer: React.FC = ({ children }) => {
  const { t } = useTranslation();
  const { isSiderCollapsed, toggleSider } = useUIStore();
  const navigate = useNavigate();

  return (
    <Sider
      collapsible
      collapsed={isSiderCollapsed}
      onCollapse={toggleSider}
      trigger={null}
      width={240}
    >
      <div className="minimal-area">
        <Tooltip
          placement="right"
          title={t(`nav.tooltip.menu.${isSiderCollapsed ? 'expand' : 'fold'}`)}
        >
          <button
            className="btn-collapse"
            onClick={() => {
              toggleSider(!isSiderCollapsed);
            }}
            title={t(`nav.tooltip.menu.${isSiderCollapsed ? 'expand' : 'fold'}`)}
          >
            <IconHB />
          </button>
        </Tooltip>
        <Tooltip placement="right" title={t('nav.tooltip.back')}>
          <button
            className="btn-back"
            onClick={() => navigate('/videos')}
            title={t('nav.tooltip.back')}
          >
            <IconArrowLeft />
          </button>
        </Tooltip>
      </div>
      {children}
      <div className="sider-footer">
        <a href="https://www.devunlimit.com" target="_blank" rel="noreferrer noopener">
          <i className="copyright">ⓒ DEVUNLIMIT Inc.</i>
        </a>
      </div>
    </Sider>
  );
};

const Sider = styled(AntdSider)`
  overflow: hidden;
  flex: 0 0 240px !important;
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  width: 240px !important;
  min-width: 240px !important;
  max-width: 240px !important;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1);
  transform: translateX(0);

  &.ant-layout-sider-collapsed {
    flex: 0 0 62px !important;
    width: 62px !important;
    min-width: 62px !important;
    max-width: 62px !important;

    .ant-layout-sider-children {
      padding: 192px 0 0 0;
    }

    .minimal-area {
      background-color: ${({ theme }) => (theme.name === 'light' ? '#fcfcfc' : '#090909')};
    }

    .ant-menu {
      width: auto;

      .ant-menu-item {
        strong {
          opacity: 0;
        }
      }
    }

    .sider-header {
      position: absolute;
      top: 118px;
      left: 0;
      width: 62px;

      .thumbnail-area {
        width: auto;

        .duration-box {
          display: none;
        }
      }

      a.thumbnail-area .overlay img {
        width: 11px;
        height: 20px;
      }

      .info-area {
        display: none;
      }
    }

    .sider-footer {
      display: none;
    }
  }

  &.sider-transition-enter {
    opacity: 0;
  }

  &.sider-transition-enter-active {
    opacity: 1;
    transition: opacity 200ms ease;
  }

  &.sider-transition-enter-done {
    opacity: 1;
    transform: translateX(0);
  }

  &.sider-transition-exit {
    transform: translateX(0);
  }

  &.sider-transition-exit-active {
    opacity: 0;
    transform: translateX(-100%);
    transition: opacity 200ms ease, transform 200ms ease;
  }

  .minimal-area {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 0;
    width: 62px;
    background-color: ${({ theme }) => theme.sidebar.minimalBgColor};

    .btn-collapse,
    .btn-back {
      path {
        fill: ${({ theme }) => theme.colors.fontSubColor};
      }
    }

    .btn-collapse {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 56px;
      transition: transform 400ms ease;
      cursor: pointer;

      &:hover {
        transform: scale(1.1);
      }
    }

    .btn-back {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 56px;
      transition: transform 400ms ease;
      cursor: pointer;

      &:hover {
        transform: scale(1.1);
      }
    }
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    padding: ${({ collapsed }) => (collapsed ? '140px' : '259px')} 0 0 0;
    background-color: ${({ theme }) => theme.sidebar.bgColor};
    color: ${({ theme }) => theme.sidebar.fontColor};
    transition: padding 200ms ease;
  }

  .ant-menu {
    flex: 1;
    width: auto;
    margin: 0;
    border-right: none;
    background-color: ${({ theme }) => theme.sidebar.bgColor};
    transition: none;

    .ant-menu-item {
      overflow: visible;
      position: relative;
      margin: 0 !important;
      padding: 0 0 0 62px !important;

      &::after {
        display: none !important;
      }

      &:active {
        background: transparent;
      }

      &.ant-menu-item-selected {
        background-color: ${({ theme }) => theme.colors.primary.bgColor2};

        .ant-menu-item-icon svg path {
          fill: ${({ theme }) => theme.colors.primary.iconSelectedColor};
        }

        .ant-menu-title-content {
          color: ${({ theme }) => (theme.name === 'light' ? '#334d6e' : '#f5f6f8')};
        }
      }

      &.ant-menu-item-disabled {
        opacity: 0.5;
      }

      & + .ant-menu-item {
        margin-top: 4px !important;
      }

      .icon {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        width: 62px;
        height: 40px;

        .anticon {
          margin-right: 0 !important;
        }

        svg {
          display: block;
          width: 24px;
          height: 24px;

          path {
            fill: ${({ theme }) => theme.colors.primary.iconColor};
          }
        }
      }

      .ant-menu-title-content {
        margin: 0;
        padding: 0 12px 0 26px;
        font-weight: normal;
        font-size: 15px;
        line-height: 1.5;
        color: ${({ theme }) => theme.colors.fontColor};
        opacity: 1;
      }
    }
  }

  .sider-header {
    position: absolute;
    top: 69px;
    left: 78px;
    opacity: 1;
    width: 146px;
    transition: all 0.1s ease;

    &.is-skeleton {
      .title,
      .desc {
        color: transparent;
        background-color: ${({ theme }) => theme.colors.skeletonColor};
      }

      .desc {
        width: 80%;
      }
    }

    .thumbnail-area {
      display: block;
      width: 146px;
      overflow: hidden;
      position: relative;
      padding-top: 56.25%;

      .image {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-size: 100% 100%;
        background-position: center;
        background-repeat: no-repeat;
      }
    }

    a.thumbnail-area {
      &:hover,
      &:focus {
        .overlay {
          opacity: 1;
        }
      }

      .overlay {
        ${scMixinAbsoluteFull};
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1;
        background-color: rgba(9, 9, 9, 0.8);
        opacity: 0;
        transition: opacity 0.2s ease;
        content: '';

        .icon {
          display: block;
          line-height: 0;
          pointer-events: none;
        }

        img {
          width: 22px;
          height: 40px;
          vertical-align: top;
        }
      }
    }

    .duration-box {
      position: absolute;
      right: 3px;
      bottom: 3px;
      transition: opacity 100ms ease;

      &[aria-hidden='true'] {
        opacity: 0;
        pointer-events: none;
      }
    }

    .duration-value {
      padding: 0 4px 1px;
      border-radius: 3px;
      background: rgba(9, 9, 9, 0.8);
      font-size: 12px;
      line-height: 1.3;
      color: #f5f6f8;
    }

    .info-area {
      margin-top: 12px;
      text-align: left;
      color: ${({ theme }) => theme.colors.fontSubColor};

      .title {
        ${scMixinEllipsis};
        display: block;
        font-size: 12px;
        line-height: 1.5;
      }

      .desc {
        ${scMixinEllipsis};
        margin-top: 4px;
        font-size: 12px;
        line-height: 1.5;
        color: ${({ theme }) => theme.colors.fontSubColor};
      }
    }

    .action-area {
      margin-top: 12px;
      text-align: center;

      .ant-btn {
        background-color: ${({ theme }) => theme.colors.primary.btnColor};
        border-color: ${({ theme }) => theme.colors.primary.btnColor};
        transition: background-color ${scVarTransitionDuration} ease,
          border-color ${scVarTransitionDuration} ease;
        color: #fff;
      }
    }
  }

  .sider-footer {
    min-width: 240px;
    padding: 24px 0 16px 86px;
    border-top: 1px solid ${({ theme }) => theme.sidebar.dividerColor};
    line-height: 16px;

    a {
      display: inline-block;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
        text-decoration-color: ${({ theme }) => theme.colors.fontColor};
      }
    }

    .copyright {
      font-family: 'Roboto', Arial, Helvetica, sans-serif;
      font-size: 13px;
      font-style: normal;
      line-height: 1.2;
      color: ${({ theme }) => theme.colors.fontColor};
    }
  }
`;

export default VideoSider;
